import React, { useEffect, useState } from 'react'
import '../../styles/close-ticket.css'
import { fetchPublic } from './ticketFetch'
import DefaultCloseForm from './forms/DefaultCloseForm'
import DcoCloseForm from './forms/DcoCloseForm'
import AfterSalesCloseForm from './forms/AfterSalesCloseForm'
import StationCloseForm from './forms/StationCloseForm'
import AarCloseForm from './forms/AarCloseForm'
import CustomerSupportCloseForm from './forms/CustomerSupportCloseForm'
import ErtCloseForm from './forms/ErtCloseForm'

function getUrlParams() {
  const path = window.location.pathname
  const match = path.match(/^\/tickets\/([^/]+)\/?$/)
  const ticketId = match ? match[1] : null
  const params = new URLSearchParams(window.location.search)
  return { ticketId, team: params.get('team') || '', action: params.get('action') || '' }
}

function resolveForm(team) {
  if (team === 'dco')         return DcoCloseForm
  if (team === 'after_sales') return AfterSalesCloseForm
  if (team === 'station')     return StationCloseForm
  if (team === 'aar')             return AarCloseForm
  if (team === 'customer_support') return CustomerSupportCloseForm
  if (team === 'ert')              return ErtCloseForm
  return DefaultCloseForm
}

/* ── Priority badge colours ── */
const PRIORITY_STYLES = {
  urgent:   { background: '#fde8e8', color: '#d93025' },
  high:     { background: '#fff0e0', color: '#c47a00' },
  medium:   { background: '#fff8e1', color: '#9a7d0a' },
  low:      { background: '#e8f5e9', color: '#2e7d32' },
}

const STATUS_STYLES = {
  in_progress: { background: '#fff8e1', color: '#9a7d0a', label: 'In Progress' },
  on_hold:     { background: '#f3e5f5', color: '#7b1fa2', label: 'On Hold'     },
  closed:      { background: '#e8f5e9', color: '#2e7d32', label: 'Closed'      },
  re_opened:   { background: '#fde8e8', color: '#d93025', label: 'Re-Opened'   },
  resolved:    { background: '#e3f2fd', color: '#1565c0', label: 'Resolved'    },
}

function cap(s) {
  if (!s) return ''
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function ScooterIllustration() {
  return (
    <svg width="90" height="80" viewBox="0 0 90 80" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      {/* Rider body */}
      <ellipse cx="57" cy="18" rx="7" ry="7" fill="#FFD0A0" />
      <rect x="50" y="25" width="14" height="18" rx="5" fill="#5c85ff" />
      {/* Helmet */}
      <ellipse cx="57" cy="15" rx="8" ry="7" fill="#3a5fc8" />
      {/* Arm */}
      <line x1="57" y1="32" x2="44" y2="36" stroke="#FFD0A0" strokeWidth="3" strokeLinecap="round" />
      {/* Scooter body */}
      <rect x="20" y="36" width="50" height="14" rx="7" fill="#c8d8ff" />
      <rect x="36" y="30" width="28" height="14" rx="6" fill="#5c85ff" />
      {/* Front fork */}
      <rect x="64" y="40" width="4" height="14" rx="2" fill="#8faaf5" />
      {/* Rear body */}
      <rect x="16" y="42" width="10" height="8" rx="4" fill="#8faaf5" />
      {/* Wheels */}
      <circle cx="22" cy="56" r="10" fill="#3a3a4a" />
      <circle cx="22" cy="56" r="5" fill="#c8d8ff" />
      <circle cx="68" cy="56" r="10" fill="#3a3a4a" />
      <circle cx="68" cy="56" r="5" fill="#c8d8ff" />
      {/* Seat */}
      <rect x="38" y="28" width="22" height="6" rx="3" fill="#2d4ab0" />
      {/* Headlight */}
      <ellipse cx="74" cy="46" rx="4" ry="3" fill="#fff9c4" />
    </svg>
  )
}

function DetailRow({ label, value }) {
  if (!value) return null
  return (
    <div className="ct-detail-row">
      <span className="ct-detail-label">{label}</span>
      <span className="ct-detail-value">{value}</span>
    </div>
  )
}

function SectionCard({ title, children }) {
  return (
    <div className="ct-section">
      <h3 className="ct-section-title">{title}</h3>
      <div className="ct-card">{children}</div>
    </div>
  )
}

const CHANNEL_MAP = {
  call: 'Phone Call (IVR)', whatsapp: 'WhatsApp',
  rider_app: 'Rider App', email: 'Email', chat: 'Chat',
}

function TicketView({ ticketData }) {
  const { ticket_information: ti, rider_details: rd, vehicle_details: vd } = ticketData
  const [fullOpen, setFullOpen] = useState(false)

  const priorityStyle = PRIORITY_STYLES[ti.priority?.toLowerCase()] || PRIORITY_STYLES.medium
  const statusInfo    = STATUS_STYLES[ti.status?.toLowerCase()]   || { background: '#f0f0f0', color: '#666', label: cap(ti.status) }

  const raisedOn        = [ti.ticket_created_date, ti.created_time].filter(Boolean).join(', ')
  const createdAt       = [ti.ticket_created_date, ti.created_time].filter(Boolean).join(' · ')
  const cncValue        = [ti.cnc_transaction_id, ti.cnc_error_code].filter(Boolean).join(' / ') || '—'
  const channelDisplay  = ti.channel ? (CHANNEL_MAP[ti.channel] || ti.channel) : '—'

  return (
    <div className="ct-ticket-view">
      {/* ── Hero header ── */}
      <div className="ct-hero">
        <div className="ct-hero-left">
          <p className="ct-ticket-number">{ti.ticket_number}</p>
          <p className="ct-category">{ti.category}</p>
          <h1 className="ct-sub-category">{ti.sub_category}</h1>
          <div className="ct-badges">
            {ti.priority && (
              <span className="ct-badge-pill" style={priorityStyle}>
                {cap(ti.priority)}
              </span>
            )}
            <span className="ct-badge-pill" style={{ background: statusInfo.background, color: statusInfo.color }}>
              {statusInfo.label}
            </span>
            {ti.ticket_stage && (
              <span className="ct-badge-pill ct-badge-stage">
                {ti.ticket_stage.toUpperCase()}
              </span>
            )}
          </div>
        </div>
        <div className="ct-hero-right">
          <ScooterIllustration />
        </div>
      </div>

      <hr className="ct-divider" />

      {/* ── Ticket Details ── */}
      <SectionCard title="Ticket Details">
        <DetailRow label="Raised on"     value={raisedOn}                />
        <DetailRow label="Station name"  value={ti.station_name}         />
        <DetailRow label="Station Code"  value={ti.station_code}         />
        <DetailRow label="Closing TAT"   value={ti.closing_tat || '—'}   />
      </SectionCard>

      {/* ── Rider Details ── */}
      {rd && (
        <SectionCard title="Rider Details">
          <DetailRow label="Rider Name" value={rd.name}            />
          <DetailRow label="VIN"        value={vd?.vin_number}     />
          <DetailRow label="Reg No."    value={vd?.registration_number} />
          <DetailRow label="Phone"      value={rd.number}          />
        </SectionCard>
      )}

      {/* ── Full Ticket Details collapsible ── */}
      <div style={{ textAlign: 'center', margin: '4px 0 8px' }}>
        <button
          type="button"
          onClick={() => setFullOpen(o => !o)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 13, color: '#0090D9', fontWeight: 500,
            padding: '6px 14px', borderRadius: 6,
          }}
        >
          {fullOpen ? '▲ Hide full ticket details' : '▼ Show full ticket details'}
        </button>
      </div>

      {fullOpen && (
        <SectionCard title="Full Ticket Information">
          <DetailRow label="Ticket ID" value={ti.ticket_number} />

          {/* Status with dot badge */}
          <div className="ct-detail-row">
            <span className="ct-detail-label">Status</span>
            <span className="ct-badge-pill" style={{ background: statusInfo.background, color: statusInfo.color, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusInfo.color, flexShrink: 0 }} />
              {statusInfo.label}
            </span>
          </div>

          {/* Priority badge */}
          {ti.priority && (
            <div className="ct-detail-row">
              <span className="ct-detail-label">Priority</span>
              <span className="ct-badge-pill" style={priorityStyle}>{cap(ti.priority)}</span>
            </div>
          )}

          <DetailRow label="Category"                              value={ti.category}                       />
          <DetailRow label="Sub-Category"                          value={ti.sub_category}                   />
          <DetailRow label="CNC transaction id / CNC error code"  value={cncValue}                          />
          <DetailRow label="Description"                           value={ti.description}                    />
          <DetailRow label="Channel"                               value={channelDisplay}                    />
          <DetailRow label="Station ID"                            value={ti.station_code || ti.station_id}  />
          <DetailRow label="Ticket Created Date"                   value={createdAt || '—'}                  />
          <DetailRow label="QIS ID"                                value={ti.qis_id || '—'}                  />
          <DetailRow label="Assigned To"                           value={ti.assigned_to}                    />
          <DetailRow label="Ticket raised by"                      value={ti.ticket_raised_by || ti.created_by} />
        </SectionCard>
      )}
    </div>
  )
}

export default function CloseTicketPage() {
  const { ticketId, team } = getUrlParams()

  const [ticketData,   setTicketData]   = useState(null)
  const [resolvedData, setResolvedData] = useState(null)
  const [loadError,    setLoadError]    = useState('')
  const [loading,      setLoading]      = useState(true)
  const [submitError,  setSubmitError]  = useState('')
  const [submitting,   setSubmitting]   = useState(false)
  const [done,         setDone]         = useState(false)
  const [doneStatus,   setDoneStatus]   = useState('')

  useEffect(() => {
    if (!ticketId) {
      setLoadError('Invalid link — no ticket ID found.')
      setLoading(false)
      return
    }
    Promise.all([
      fetchPublic(`/api/tickets/ticket/get-ticket/${ticketId}/`),
      fetchPublic(`/api/tickets/ticket/get-resolved-data/${ticketId}/`).catch(() => null),
    ])
      .then(([ticket, resolved]) => {
        setTicketData(ticket)
        if (resolved) setResolvedData(resolved)
      })
      .catch((err) => setLoadError(
        err.status === 400 ? 'Ticket not found.' : 'Failed to load ticket. Please try again.'
      ))
      .finally(() => setLoading(false))
  }, [ticketId])

  async function handleSubmit(payload) {
    setSubmitError('')
    setSubmitting(true)
    try {
      const isFormData = payload instanceof FormData
      await fetchPublic(`/api/tickets/ticket/resolve-ticket/${ticketId}/`, {
        method: 'POST',
        body: isFormData ? payload : JSON.stringify(payload),
      })
      setDoneStatus(isFormData ? (payload.get('ticket_status') || '') : (payload.ticket_status || ''))
      setDone(true)
    } catch (err) {
      setSubmitError(
        err.status === 400
          ? 'Please check your inputs and try again.'
          : 'Something went wrong. Please retry.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  const FormComponent = resolveForm(team)

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="ct-page">
        <div className="ct-shell">
          <div className="ct-center-state">
            <span className="ct-spinner" />
            <p>Loading ticket…</p>
          </div>
        </div>
      </div>
    )
  }

  /* ── Not found / error ── */
  if (loadError) {
    return (
      <div className="ct-page">
        <div className="ct-shell">
          <div className="ct-center-state">
            <span className="ct-state-icon ct-state-icon--error">&#9888;</span>
            <p className="ct-state-msg">{loadError}</p>
          </div>
        </div>
      </div>
    )
  }

  /* ── Success ── */
  if (done) {
    const statusLabel = doneStatus === 'on_hold' ? 'On Hold' : doneStatus === 'closed' ? 'Closed' : 'Updated'
    return (
      <div className="ct-page">
        <div className="ct-shell">
          <div className="ct-center-state">
            <span className="ct-state-icon ct-state-icon--success">&#10003;</span>
            <h2>Ticket {statusLabel}</h2>
            <p>Ticket {ticketData?.ticket_information?.ticket_number} has been marked as {statusLabel.toLowerCase()} successfully.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="ct-page">
      <div className="ct-shell">
        <TicketView ticketData={ticketData} />

        <hr className="ct-divider" />

        {submitError && <div className="ct-submit-error">{submitError}</div>}

        <FormComponent
          ticket={ticketData}
          ticketId={ticketId}
          resolvedData={resolvedData}
          readOnly={ticketData?.ticket_information?.status === 'closed'}
          team={team}
          onSubmit={handleSubmit}
          submitting={submitting}
        />
      </div>
    </div>
  )
}
